// Orchestrator sign-in gate for the shell. Sits between the public surfaces and
// the shell (a signed-in orchestrator is required before any real data shows).
//
// SIGN-IN IS GOOGLE OR APPLE (the hard cut from GitHub-only). GitHub is now a
// per-project CONNECTION (repo read access) made later in the connect flow —
// never sign-in. Each provider button hands the browser to the backend's
// `/auth/oauth/<provider>/start`; the provider callback redirects back to
// `/signin#token=…`, which this screen reads (the fragment-parse path is
// unchanged from the GitHub era), persists via the kept `saveSession` (under
// `wt_token`), and resolves — every downstream real-data call rides it through
// the Bearer header exactly as before. Nothing here mints or fakes a token; the
// JWT comes from the backend. (The device-code paste fallback on the callback
// page is DESKTOP-only — the web callback always lands, so no code UI here.)
//
// PRESENTATION: the newspaper-receipt design language (the landing idiom) via the
// shared ReceiptKit — washed paper substrate + grain, the WAYTIDE wordmark, a
// [ SIGN IN ] eyebrow, dashed coupon tear-rules, the filled receipt buttons, and
// the one reserved amber as the error edge-tick stamp.

import { useEffect, useState } from "react";
import { Download } from "lucide-react";
import { Link } from "react-router-dom";
import {
  clearSession,
  getToken,
  getUser,
  oauthStartUrl,
  saveSession,
  type SessionUser,
} from "../../modules/api/client";
import type { OAuthProvider } from "@waytide/core";
import { parseAuthCallback, authErrorMessage } from "./authCallback";
import {
  CornerTicks,
  Eyebrow,
  ReceiptPage,
  TearRule,
  Wordmark,
} from "../receipt/ReceiptKit";
import "./auth.css";

/** Reactive view of the kept session. `signedIn` is derived from the
 * kept `wt_token` the api-client reads on every authed request, so the
 * gate and the requests agree on a single source of truth. */
export interface OrchestratorAuth {
  signedIn: boolean;
  user: SessionUser | null;
  /** Re-read the kept session after a sign-in/sign-out. */
  refresh: () => void;
  signOut: () => void;
}

export function useOrchestratorAuth(): OrchestratorAuth {
  // The kept session lives in localStorage (wt_token), not React state.
  // Mirror just the signed-in flag into state so a sign-in/out triggers
  // a re-render; `bump` re-reads the kept token after saveSession /
  // clearSession mutate it.
  const [signedIn, setSignedIn] = useState(() => Boolean(getToken()));
  const bump = () => setSignedIn(Boolean(getToken()));
  return {
    signedIn,
    user: signedIn ? getUser() : null,
    refresh: bump,
    signOut: () => {
      clearSession();
      bump();
    },
  };
}

const PROVIDER_LABEL: Record<OAuthProvider, string> = {
  google: "Google",
  apple: "Apple",
};

/** The Google/Apple orchestrator sign-in. On mount it consumes an OAuth callback
 * fragment (persisting the session + scrubbing the hash, or surfacing a calm
 * error); otherwise it shows the two "Continue with …" buttons that start the
 * flow. `onSignedIn` fires once the kept `saveSession` has stored the JWT. */
export function OrchestratorSignIn({ onSignedIn }: { onSignedIn: () => void }) {
  const [error, setError] = useState("");
  const [pending, setPending] = useState<OAuthProvider | null>(null);

  // Consume an OAuth callback fragment exactly once, on mount. A signed
  // session is persisted via the kept store and bubbled up; an error is shown.
  // Either way the hash is scrubbed so a reload doesn't re-process (or leak the
  // token in the address bar).
  useEffect(() => {
    if (typeof window === "undefined") return;
    const result = parseAuthCallback(window.location.hash);
    if (result.kind === "none") return;
    // Scrub the fragment from the URL without a navigation/history entry.
    window.history.replaceState(
      null,
      "",
      window.location.pathname + window.location.search,
    );
    if (result.kind === "session") {
      saveSession(result.session);
      onSignedIn();
    } else {
      setError(authErrorMessage(result.code));
    }
  }, [onSignedIn]);

  function continueWith(provider: OAuthProvider) {
    if (pending) return;
    setError("");
    setPending(provider);
    // Full-page handoff to the backend, which 302s to the provider. No fetch —
    // the session is established on the callback's redirect back here (no
    // `redirect` param: the backend's default web `/signin#token=…` return).
    window.location.assign(oauthStartUrl(provider));
  }

  return (
    <ReceiptPage className="wt-auth">
      <main className="wt-auth__card">
        <CornerTicks />
        <div className="wt-auth__head">
          <Wordmark />
          <span className="wt-auth__tag">agent supervision</span>
        </div>

        <Eyebrow label="SIGN IN" />
        <TearRule />

        <p className="wt-auth__lede">Sign in to see your sessions.</p>

        <div className="wt-auth__providers">
          {(["google", "apple"] as const).map((provider) => (
            <button
              key={provider}
              type="button"
              onClick={() => continueWith(provider)}
              disabled={pending !== null}
              className="wt-receipt-button wt-auth__provider"
              data-testid={`orchestrator-${provider}`}
            >
              {provider === "google" ? <GoogleMark /> : <AppleMark />}
              {pending === provider
                ? `Redirecting to ${PROVIDER_LABEL[provider]}…`
                : `Continue with ${PROVIDER_LABEL[provider]}`}
            </button>
          ))}
        </div>

        <p className="wt-auth__note">
          Waytide reads your repo to give the Operators your project&apos;s
          history. You connect GitHub per project when you connect a session
          &mdash; sign-in doesn&apos;t touch your code.
        </p>

        {error && (
          <p className="wt-auth__error" data-testid="orchestrator-signin-error">
            {error}
          </p>
        )}

        <TearRule />

        {/* Waytide starts on the desktop — a new user gets the app FIRST. The
            /download route lives outside the orchestrator gate, so this works pre-auth. */}
        <div className="wt-auth__get">
          <span className="wt-auth__get-lead">
            new to waytide? it starts on the desktop.
          </span>
          <Link
            to="/download"
            data-testid="signin-get-desktop"
            className="wt-receipt-link"
          >
            <Download size={15} aria-hidden="true" />
            get the desktop app — no sign-in needed
          </Link>
        </div>
      </main>
    </ReceiptPage>
  );
}

/** The Google "G" glyph (inline SVG, currentColor; no extra icon dependency —
 * monochrome to stay inside the token-pure receipt idiom). */
function GoogleMark() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M21.6 12.227c0-.709-.064-1.39-.182-2.045H12v3.868h5.382a4.6 4.6 0 0 1-1.996 3.018v2.51h3.232c1.891-1.742 2.982-4.305 2.982-7.35z" />
      <path d="M12 21.75c2.7 0 4.964-.895 6.618-2.423l-3.232-2.509c-.895.6-2.04.955-3.386.955-2.605 0-4.81-1.76-5.595-4.123H3.064v2.59A9.996 9.996 0 0 0 12 21.75z" />
      <path d="M6.405 13.65a6.01 6.01 0 0 1 0-3.841V7.218H3.064a9.996 9.996 0 0 0 0 8.982l3.34-2.55z" />
      <path d="M12 5.686c1.468 0 2.786.505 3.823 1.496l2.868-2.868C16.96 2.699 14.7 1.75 12 1.75a9.996 9.996 0 0 0-8.936 5.468l3.34 2.591C7.19 7.446 9.396 5.686 12 5.686z" />
    </svg>
  );
}

/** The Apple mark glyph (inline SVG, currentColor; no extra icon dependency). */
function AppleMark() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M16.365 12.77c.024 2.605 2.285 3.472 2.31 3.483-.019.061-.361 1.236-1.191 2.45-.718 1.05-1.463 2.095-2.637 2.117-1.153.021-1.524-.684-2.843-.684-1.318 0-1.73.663-2.822.705-1.133.043-1.996-1.135-2.72-2.18-1.478-2.138-2.608-6.042-1.091-8.677.753-1.309 2.1-2.137 3.562-2.159 1.112-.021 2.162.749 2.842.749.68 0 1.956-.926 3.297-.79.561.023 2.137.227 3.149 1.709-.081.05-1.88 1.098-1.856 3.277zM14.2 6.37c.601-.728 1.006-1.741.895-2.749-.866.035-1.913.577-2.534 1.304-.557.644-1.045 1.675-.913 2.663.965.075 1.951-.49 2.552-1.218z" />
    </svg>
  );
}
